// src/pages/DevPage.jsx
import React, { useState, useEffect } from 'react';
import '../DevPage.css';

const DEVOPS_TEAM_ID = 2; // adjust to your actual DevOps team ID
const FILTERED_STATUSES = ['Not Started', 'In Progress'];

const DevPage = () => {
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [newMessage, setNewMessage] = useState('');

  // Fetch tasks from backend filtered by DevOps team on component mount
  useEffect(() => {
    async function fetchTasks() {
      try {
        const response = await fetch(`/tasks?team_id=${DEVOPS_TEAM_ID}`);
        if (!response.ok) throw new Error('Failed to fetch tasks');
        const data = await response.json();
        setTasks(data);
      } catch (err) {
        console.error(err);
      }
    }
    fetchTasks();
  }, []);

  // Filter tasks by status (e.g. Not Started)
  const filteredTasks = tasks.filter(task => FILTERED_STATUSES.includes(task.status_name));

  // Fetch messages for a task and attach to selectedTask
  const fetchMessagesForTask = async (taskId) => {
    try {
      const res = await fetch(`/tasks/${taskId}/messages`);
      if (!res.ok) throw new Error('Failed to fetch messages');
      const data = await res.json();

      // Map DB messages to UI format
      const formattedMessages = data.map(msg => ({
        from: msg.type_id === 1 ? 'Them' : 'You',
        text: msg.message_text,
      }));

      setSelectedTask(prev => ({ ...prev, messages: formattedMessages }));
    } catch (err) {
      console.error('Error loading messages:', err);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedTask) return;

    try {
      // 1. Save message to DB (outbound)
      const saveRes = await fetch(`/tasks/${selectedTask.id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message_text: newMessage,
          type_id: 2, // outbound
        }),
      });
      if (!saveRes.ok) throw new Error('Failed to save message');

      // 2. Send email notification
      const emailRes = await fetch(`/tasks/${selectedTask.id}/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: `New message regarding: ${selectedTask.name || selectedTask.text}`,
          message: newMessage,
        }),
      });
      if (!emailRes.ok) throw new Error('Failed to send email');

      // 3. Update UI optimistically
      setSelectedTask(prev => ({
        ...prev,
        messages: [...(prev.messages || []), { from: 'You', text: newMessage }],
      }));
      setNewMessage('');
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Failed to send message.');
    }
  };

  const handleApprove = () => {
    if (!selectedTask) return;
    alert(`Approved: ${selectedTask.name || selectedTask.text || 'Task'}`);
    // TODO: Implement approval backend call
  };

  return (
    <div className="dev-page">
      <h2>DevOps Tasks</h2>

      <div className="main-content">
        <div className="item-list">
          {filteredTasks.length === 0 && (<p>No "{FILTERED_STATUSES.join(' or ')}" tasks found for DevOps team.</p>)}
          {filteredTasks.map(task => (
            <div
              key={task.id}
              className="item-cell"
              onClick={() => {
                setSelectedTask(task);
                fetchMessagesForTask(task.id);
              }}
              style={{ cursor: 'pointer' }}
            >
              {task.name || task.text}
            </div>
          ))}
        </div>

        {selectedTask && (
          <div className="item-detail">
            <h3>{selectedTask.name || selectedTask.text}</h3>
            {selectedTask.description && (
              <p><strong>Description:</strong> {selectedTask.description}</p>
            )}
            <p><strong>Status:</strong> {selectedTask.status_name || 'N/A'}</p>
            <p><strong>Team:</strong> {selectedTask.team_name || 'N/A'}</p>

            <div className="chat-window">
              <div className="messages">
                {(selectedTask.messages || []).map((msg, idx) => (
                  <div key={idx} className={`message ${msg.from === 'You' ? 'sent' : 'received'}`}>
                    <strong>{msg.from}:</strong> {msg.text}
                  </div>
                ))}
              </div>

              <div className="message-input">
                <input
                  type="text"
                  value={newMessage}
                  placeholder="Type a message..."
                  onChange={(e) => setNewMessage(e.target.value)}
                />
                <button onClick={handleSendMessage}>Send</button>
              </div>
            </div>

            <button className="approve-btn" onClick={handleApprove}>Approve</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DevPage;
